import React, { useEffect, useState } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import { muxService } from '../services/muxService';
import { supabase } from '../services/supabase';
import LiveChat from '../components/LiveChat';
import '../styles/LiveStreams.css';

const LiveStreams = () => {
  const [streams, setStreams] = useState([]);
  const [selectedStream, setSelectedStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLiveStreams();
    const interval = setInterval(fetchLiveStreams, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveStreams = async () => {
    try {
      const response = await muxService.listLiveStreams();
      const allStreams = response.data || [];
      const activeStreams = allStreams.filter(s => s.status === 'active');

      const streamIds = activeStreams.map(s => s.id);

      const { data: metadata, error: metaError } = await supabase
        .from('streams_metadata')
        .select('mux_stream_id, display_title, stream_name')
        .in('mux_stream_id', streamIds);

      if (metaError) {
        console.warn('Could not fetch stream metadata:', metaError);
      }

      const metadataMap = {};
      (metadata || []).forEach(m => {
        metadataMap[m.mux_stream_id] = {
          display_title: m.display_title,
          stream_name: m.stream_name
        };
      });

      const streamsWithMetadata = activeStreams.map(s => ({
        ...s,
        metadata: metadataMap[s.id] || {}
      }));

      setStreams(streamsWithMetadata);

      if (streamsWithMetadata.length > 0 && !selectedStream) {
        setSelectedStream(streamsWithMetadata[0]);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching live streams:', err);
      setError('Failed to load live streams. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="live-streams-page">
        <div className="loading">Loading live streams...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-streams-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (streams.length === 0) {
    return (
      <div className="live-streams-page">
        <div className="no-streams">
          <h2>No Live Streams Currently</h2>
          <p>Check back soon for live games!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="live-streams-page">
      <div className="streams-container">
        <div className="main-content">
          <div className="player-section">
            <div className="player-wrapper">
              {selectedStream ? (
                <>
                  <MuxPlayer
                    playbackId={muxService.getPlaybackId(selectedStream)}
                    primaryColor="#FFFFFF"
                    secondaryColor="#000000"
                    style={{ width: '100%', aspectRatio: '16 / 9' }}
                  />
                </>
              ) : (
                <div className="no-player">No stream selected</div>
              )}
            </div>
            {selectedStream && (
              <div className="stream-title">
                <h2>{selectedStream.metadata?.display_title || selectedStream.metadata?.stream_name || selectedStream.id}</h2>
                <span className="live-badge">● LIVE</span>
              </div>
            )}
          </div>

          {selectedStream && (
            <div className="chat-section">
              <LiveChat 
                streamId={selectedStream.id}
                username="Guest"
                isAdmin={false}
              />
            </div>
          )}
        </div>

        <div className="streams-list-section">
          <h3>Active Streams</h3>
          <div className="streams-list">
            {streams.map(stream => (
              <div
                key={stream.id}
                className={`stream-item ${selectedStream?.id === stream.id ? 'active' : ''}`}
                onClick={() => setSelectedStream(stream)}
              >
                <div className="stream-item-content">
                  <div className="stream-item-header">
                    <h4>{stream.metadata?.display_title || stream.metadata?.stream_name || stream.id}</h4>
                    <span className="live-indicator">● LIVE</span>
                  </div>
                  <p className="stream-status">Status: {stream.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreams;
